import { zeroErrorMessage } from "@/lib/zero-error";
import { useMemo } from "react";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import {
  MeetingNoteWithTags,
  toLegacyMeetingNote,
  type ZeroMeetingNoteRow,
} from "./useMeetingNotes";

export interface MeetingNoteWithContext extends MeetingNoteWithTags {
  project?: { id: string; name: string; number: string | null } | null;
  project_group?: { id: string; name: string; number: string | null } | null;
}

type ZeroMeetingNoteWithContextRow = ZeroMeetingNoteRow & {
  project?: {
    id: string;
    name: string;
    number?: string | null;
    eventStartDate?: number | string | null;
  } | null;
  projectGroup?: {
    id: string;
    name: string;
    number?: string | null;
  } | null;
};

function toLegacyMeetingNoteWithContext(
  row: ZeroMeetingNoteWithContextRow,
): MeetingNoteWithContext {
  return {
    ...toLegacyMeetingNote(row),
    project: row.project
      ? {
          id: row.project.id,
          name: row.project.name,
          number: row.project.number ?? null,
        }
      : null,
    project_group: row.projectGroup
      ? {
          id: row.projectGroup.id,
          name: row.projectGroup.name,
          number: row.projectGroup.number ?? null,
        }
      : null,
  };
}

export function useAllMeetingNotes() {
  const [data, details] = useZeroQuery(appQueries.meetingNotes.all());
  const notes = useMemo(
    () =>
      ((data ?? []) as ZeroMeetingNoteWithContextRow[]).map(
        toLegacyMeetingNoteWithContext,
      ),
    [data],
  );

  return {
    data: notes,
    isLoading: details.type !== "complete" && notes.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}
